import { en, ja, MessageKey } from './messages';

export type Locale = 'en' | 'ja';
export type Parameters = Record<string, string | number>;
export const LANGUAGE_STORAGE_KEY = 'nova-rally-language';

export function detectLanguage(saved: string | null, languages: readonly string[]): Locale {
  if (saved === 'en' || saved === 'ja') return saved;
  return /^ja(?:-|$)/i.test(languages[0] || '') ? 'ja' : 'en';
}

export function readLanguage(): Locale {
  let saved: string | null = null;
  try { saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY); } catch { /* Storage is optional. */ }
  return detectLanguage(saved, typeof navigator === 'undefined' ? [] : navigator.languages || [navigator.language]);
}

export function template(locale: Locale, key: MessageKey): string {
  return (locale === 'ja' ? ja[key] : en[key]) || en[key];
}

export function translate(locale: Locale, key: MessageKey, values: Parameters = {}): string {
  return template(locale, key).replace(/\{(\w+)\}/g, (placeholder, name) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : placeholder);
}

/** Only use for site-owned labels/descriptions, never user content or identifiers. */
export function translateOwnedText(locale: Locale, text: string): string {
  if (typeof text !== 'string') return '';
  if (locale === 'en') return text;
  if (Object.prototype.hasOwnProperty.call(en, text)) return translate(locale, text as MessageKey);
  // Legacy services store English messages. Match only complete, known templates;
  // keep their parameters intact and translate at render time, never in game state.
  for (const { key, names, pattern } of ownedTemplates) {
    const match = pattern.exec(text);
    if (match) return translate(locale, key, Object.fromEntries(names.map((name, i) => [name, match[i + 1]])));
  }
  return text;
}

const ownedTemplates = (Object.keys(en) as MessageKey[]).filter(key => /\{\w+\}/.test(key)).map(key => {
  const names: string[] = [];
  const source = key.split(/(\{\w+\})/g).map(part => {
    if (/^\{\w+\}$/.test(part)) { names.push(part.slice(1, -1)); return '(.+?)'; }
    return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }).join('');
  return { key, names, pattern: new RegExp(`^${source}$`) };
});
